import { getGetSystemLogsQueryKey, useGetSystemLogs } from '@agos/api-client/reporting'
import { T, formatDateTime, tt } from '@agos/shared'
import { EmptyState, PageHeader, StatusBadge, cn } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, RefreshCw, ScrollText, Search, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  LEVEL_OPTIONS,
  SOURCE_OPTIONS,
  countLevels,
  describeContext,
  filterLogs,
  levelBadge,
  sourceLabel,
  type KasaLog,
} from './kasa-log'

/**
 * `/kasa-logs` — what the registers have been saying.
 *
 * Somebody opens this because a receipt did not print. So the point is not the
 * list, it is reaching the one line that explains it — which is why the search
 * covers the message, the context and the device id rather than the message alone.
 *
 * Ported from legacy `pages/kasa-logs.tsx`. Twenty-second poll, copied (§14.1):
 * a register that has just failed is failing *now*.
 */

const REFRESH_MS = 20_000
const LOG_LIMIT = 200

const LEVEL_ICONS: Record<string, typeof CheckCircle2> = {
  info: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
}

const FILTER_CLASSES =
  'px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

export function KasaLogsPage() {
  const queryClient = useQueryClient()
  const [source, setSource] = useState('all')
  const [level, setLevel] = useState('all')
  const [search, setSearch] = useState('')

  const { data, isLoading, dataUpdatedAt } = useGetSystemLogs(
    {
      // `kasa` is the prefix both registers log under, so it is the whole-module
      // filter as well as the default.
      source: source === 'all' ? 'kasa' : source,
      limit: LOG_LIMIT,
    },
    { query: { refetchInterval: REFRESH_MS } },
  )

  const logs = useMemo(() => {
    const payload = data as { rows?: unknown } | unknown[] | undefined
    const rows = Array.isArray(payload) ? payload : (payload?.rows ?? [])
    return Array.isArray(rows) ? (rows as KasaLog[]) : []
  }, [data])

  const shown = useMemo(() => filterLogs(logs, { level, search }), [logs, level, search])
  const counts = countLevels(logs)

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Logy kasy')}
        description={tt('Čo hlásili fiškálne kasy.')}
        icon={<ScrollText className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {dataUpdatedAt > 0 && (
              <span className="text-[11px] text-muted-foreground" aria-live="polite">
                {formatDateTime(dataUpdatedAt)}
              </span>
            )}
            <button
              type="button"
              onClick={() =>
                void queryClient.invalidateQueries({ queryKey: getGetSystemLogsQueryKey() })
              }
              aria-label={tt('Obnoviť')}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tt('Číslo objednávky, správa, zariadenie...')}
            aria-label={tt('Hľadať v logoch')}
            className={cn(FILTER_CLASSES, 'w-full pl-9')}
          />
        </div>
        <select
          value={source}
          onChange={(event) => setSource(event.target.value)}
          aria-label={tt('Kasa')}
          className={FILTER_CLASSES}
        >
          {SOURCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={level}
          onChange={(event) => setLevel(event.target.value)}
          aria-label={tt('Závažnosť')}
          className={FILTER_CLASSES}
        >
          {LEVEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* An error in two hundred lines is invisible without this. */}
        <div className="flex items-center gap-1.5 ml-auto">
          {counts.error > 0 && <StatusBadge color="red" label={`${counts.error}`} icon={XCircle} />}
          {counts.warning > 0 && (
            <StatusBadge color="orange" label={`${counts.warning}`} icon={AlertTriangle} />
          )}
          <StatusBadge color="emerald" label={`${counts.info}`} icon={CheckCircle2} />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-1.5">
          {[1, 2, 3, 4, 5].map((placeholder) => (
            <div
              key={placeholder}
              className="h-12 rounded-xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<ScrollText className="w-10 h-10" />}
            title={tt('Žiadne záznamy')}
            description={tt('Kasa v tomto filtri nič nehlásila.')}
          />
        </div>
      ) : (
        <div className="space-y-1.5" data-kasa-logs>
          {shown.map((log) => {
            const badge = levelBadge(log.level)
            const Icon = LEVEL_ICONS[log.level ?? ''] ?? CheckCircle2
            const context = describeContext(log.context)

            return (
              <div
                key={log.id}
                className={cn(
                  'bg-card border rounded-xl px-3 py-2 flex items-start gap-3',
                  log.level === 'error'
                    ? 'border-red-500/30'
                    : log.level === 'warning'
                      ? 'border-amber-500/30'
                      : 'border-border',
                )}
              >
                <Icon
                  className={cn(
                    'w-4 h-4 shrink-0 mt-0.5',
                    log.level === 'error'
                      ? 'text-red-500'
                      : log.level === 'warning'
                        ? 'text-amber-500'
                        : 'text-emerald-500',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{log.message}</p>
                  {context && (
                    <p className="text-[11px] text-muted-foreground font-mono break-all">
                      {context}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0 space-y-0.5">
                  <StatusBadge color={badge.color} label={sourceLabel(log.source)} />
                  <p className="text-[10px] text-muted-foreground tabular-nums">
                    {log.createdAt ? formatDateTime(log.createdAt) : ''}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {shown.length > 0 && shown.length !== logs.length && (
        <p className="text-[11px] text-muted-foreground">
          {shown.length} {tt('z')} {logs.length} <T>záznamov</T>
        </p>
      )}
    </div>
  )
}
